import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CardActions, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';

import {
  Button,
  CircularProgress,
  Divider,
  Typography,
  Card,
  CardContent,
} from '@occc/ui-design-system';
import { Container, PageHeader, useAppLayoutContextConsumer } from '@occc-app/design-system';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';

import { EditFormContent } from './EditFormContent';
import { getBreadcrumbLinks } from './utils';

import { WalletData, WalletFormValues } from '../types';
import { validateWallet } from '../validation';

const PAGE_TITLE = 'Update Wallet';

type EditWalletPageProps = {
  walletData: WalletData;
  initialValues: Partial<WalletFormValues>;
  onSubmit: (
    values: WalletFormValues,
    form: FormApi<WalletFormValues, Partial<WalletFormValues>>,
  ) => void;
  isUserPermittedToEditWallet: boolean;
};

export const EditWalletPage = ({
  walletData,
  onSubmit,
  initialValues,
  isUserPermittedToEditWallet,
}: EditWalletPageProps) => {
  const navigate = useNavigate();
  const editWalletPageTitle = isUserPermittedToEditWallet
    ? 'Update Wallet'
    : 'Wallet Configurations';

  const breadcrumbLinks = React.useMemo(() => {
    return getBreadcrumbLinks(editWalletPageTitle);
  }, [editWalletPageTitle]);

  useAppLayoutContextConsumer(breadcrumbLinks);

  // TODO: move Container and PageHeader to level up and replace it along with Helmet by Page component
  const renderForm = React.useCallback(
    ({ handleSubmit, valid, submitting, pristine, values }: FormRenderProps<WalletFormValues>) => (
      <form onSubmit={handleSubmit}>
        <Container variant='md' includesPageHeader>
          <PageHeader title={editWalletPageTitle} />
          <Card>
            <CardContent>
              <Box>
                <EditFormContent values={values} />
              </Box>
            </CardContent>

            <Divider />

            <CardActions>
              <Button role='button' onClick={() => navigate(-1)}>
                Cancel
              </Button>
              {isUserPermittedToEditWallet && (
                <Button
                  variant='contained'
                  color='primary'
                  type='submit'
                  disabled={!valid || pristine || submitting}
                >
                  Save Changes
                </Button>
              )}
            </CardActions>
          </Card>
        </Container>
      </form>
    ),
    [editWalletPageTitle, isUserPermittedToEditWallet, navigate],
  );

  const renderContent = () => {
    const { survey, wallet } = walletData;
    const error = wallet.error || survey.error;

    const walletLoading = wallet.loading && !wallet.data;
    const surveyLoading = survey.loading && !survey.data;
    const loading = walletLoading || surveyLoading;

    if (error) {
      return <Typography color='error'>Something went wrong. Please try again.</Typography>;
    }

    if (loading) {
      return <CircularProgress />;
    }

    if (!wallet) {
      return (
        <Typography color='error'>No data. Something went wrong. Please try again.</Typography>
      );
    }

    return (
      <CanEditFormFields isEditable={isUserPermittedToEditWallet}>
        <Form<WalletFormValues>
          onSubmit={onSubmit}
          render={renderForm}
          validate={validateWallet}
          initialValues={initialValues}
          keepDirtyOnReinitialize
        />
      </CanEditFormFields>
    );
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
      </Helmet>

      {renderContent()}
    </>
  );
};
